import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authService, inviteService, paymentService } from '../services/api';
import dividuText from '../assets/dividu.svg';

/**
 * Navbar moderna com design elegante e responsivo.
 */
const NavbarComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = authService.getCurrentUser();
  const isAuthenticated = !!user.userId;
  const [menuOpen, setMenuOpen] = useState(false);
  const [inviteCount, setInviteCount] = useState(0);
  const [paymentsPendingCount, setPaymentsPendingCount] = useState(0);

  // Carrega contagem de convites e pagamentos pendentes
  useEffect(() => {
    if (isAuthenticated) {
      loadInviteCount();
      loadPaymentsPendingCount();

      // Atualiza a cada 30 segundos
      const interval = setInterval(() => {
        loadPaymentsPendingCount();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, location.pathname]);

  const loadInviteCount = async () => {
    try {
      const count = await inviteService.countMyInvites();
      setInviteCount(count);
    } catch (err) {
      console.error('Erro ao carregar convites:', err);
    }
  };

  const loadPaymentsPendingCount = async () => {
    try {
      const count = await paymentService.countAwaitingConfirmation();
      setPaymentsPendingCount(count);
    } catch (err) {
      console.error('Erro ao carregar pagamentos pendentes:', err);
    }
  };

  const handleLogout = () => {
    authService.logout();
    setMenuOpen(false);
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const isActive = (path) => location.pathname === path;

  // Não mostra navbar nas páginas de auth
  if (['/login', '/register', '/verify-email', '/forgot-password', '/reset-password'].includes(location.pathname)) {
    return null;
  }

  return (
    <nav className="navbar-custom">
      <div className="navbar-container">
        {/* Brand */}
        <Link to="/groups" className="navbar-brand-custom">
          <img src={dividuText} alt="Dividu" className="navbar-brand-text" style={{ height: '35px', width: 'auto' }} />
        </Link>

        {/* Mobile Toggle */}
        <button
          className="navbar-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <i className={menuOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'}></i>
        </button>

        {/* Navigation */}
        {isAuthenticated && (
          <div className={`navbar-menu ${menuOpen ? 'open' : ''}`}>
            {/* Nav Links */}
            <Link
              to="/groups"
              className={`nav-link-custom ${isActive('/groups') ? 'active' : ''}`}
              onClick={() => setMenuOpen(false)}
            >
              <i className="fa-solid fa-folder-open"></i> Meus Grupos
            </Link>

            <Link
              to="/invites"
              className={`nav-link-custom ${isActive('/invites') ? 'active' : ''}`}
              onClick={() => setMenuOpen(false)}
              style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <span style={{ position: 'relative' }}>
                <i className="fa-solid fa-envelope"></i>
                {inviteCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -8,
                    right: -10,
                    background: '#EF4444',
                    color: 'white',
                    borderRadius: '50%',
                    minWidth: 18,
                    height: 18,
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    border: '2px solid var(--bg-primary, #fff)'
                  }}>
                    {inviteCount > 99 ? '99+' : inviteCount}
                  </span>
                )}
              </span>
              <span>Convites</span>
            </Link>

            <Link
              to="/payments/pending-confirmation"
              className={`nav-link-custom ${isActive('/payments/pending-confirmation') ? 'active' : ''}`}
              onClick={() => setMenuOpen(false)}
              style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <span style={{ position: 'relative' }}>
                <i className="fa-solid fa-bell"></i>
                {paymentsPendingCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -8,
                    right: -10,
                    background: '#F59E0B',
                    color: 'white',
                    borderRadius: '50%',
                    minWidth: 18,
                    height: 18,
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    border: '2px solid var(--bg-primary, #fff)',
                    animation: 'pulse 2s infinite'
                  }}>
                    {paymentsPendingCount > 99 ? '99+' : paymentsPendingCount}
                  </span>
                )}
              </span>
              <span>Confirmar Pagamentos</span>
            </Link>

            <Link
              to="/settings/pix"
              className={`nav-link-custom ${isActive('/settings/pix') ? 'active' : ''}`}
              onClick={() => setMenuOpen(false)}
            >
              <i className="fa-solid fa-key"></i> Minha chave PIX
            </Link>

            {/* Mobile User Info */}
            <div className="navbar-user-mobile">
              <Link
                to="/profile"
                className="user-info-mobile"
                onClick={() => setMenuOpen(false)}
                style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                <div className="user-avatar">
                  {getInitials(user.name)}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 600 }}>{user.name}</span>
                  <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Minha Conta</span>
                </div>
              </Link>
              <button className="btn-logout-mobile" onClick={handleLogout}>
                <i className="fa-solid fa-right-from-bracket"></i> Sair
              </button>
            </div>

            {/* Desktop User Info */}
            <div className={`navbar-user-desktop ${menuOpen ? 'active' : ''}`} style={{ position: 'relative' }}>
              <button
                className="user-menu-btn"
                onClick={() => setMenuOpen(!menuOpen)}
                style={{
                  background: 'none',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '8px',
                  color: 'inherit'
                }}
              >
                <div className="user-avatar">
                  {getInitials(user.name)}
                </div>
                <span className="user-name-desktop">
                  {user.name?.split(' ')[0]}
                </span>
                <i className={`fa-solid fa-chevron-down ${menuOpen ? 'fa-rotate-180' : ''}`} style={{ fontSize: '0.8rem', transition: 'transform 0.2s' }}></i>
              </button>

              {/* Dropdown Menu */}
              {menuOpen && (
                <div className="dropdown-menu-custom" style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '10px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  minWidth: '220px',
                  zIndex: 1000,
                  overflow: 'hidden',
                  border: '1px solid rgba(0,0,0,0.05)'
                }}>
                  <div style={{ padding: '16px', borderBottom: '1px solid #f3f4f6' }}>
                    <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-primary)' }}>{user.name}</p>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</p>
                  </div>

                  <Link
                    to="/profile"
                    onClick={() => setMenuOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px 16px',
                      color: 'var(--text-primary)',
                      textDecoration: 'none',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <i className="fa-solid fa-user-gear" style={{ width: '20px', color: '#6b7280' }}></i>
                    Minha Conta
                  </Link>

                  <button
                    onClick={handleLogout}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px 16px',
                      width: '100%',
                      background: 'none',
                      border: 'none',
                      color: '#EF4444',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '1rem',
                      borderTop: '1px solid #f3f4f6',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <i className="fa-solid fa-right-from-bracket" style={{ width: '20px' }}></i>
                    Sair
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {!isAuthenticated && (
          <div className={`navbar-menu ${menuOpen ? 'open' : ''}`}>
            <Link to="/login" className="btn btn-logout" onClick={() => setMenuOpen(false)}>
              Entrar
            </Link>
            <Link to="/register" className="btn btn-primary" style={{ boxShadow: 'none' }} onClick={() => setMenuOpen(false)}>
              Cadastrar
            </Link>
          </div>
        )}
      </div>

      {/* Overlay para fechar menu mobile */}
      {menuOpen && <div className="navbar-overlay" onClick={() => setMenuOpen(false)} />}
    </nav>
  );
};

export default NavbarComponent;
